import { useEffect, useRef, useState } from 'react'
import { api } from '../../services/api.js'
import UpgradeInline from '../shared/UpgradeInline.jsx'

/**
 * Origin AI — research-launcher panel on PersonPage.
 *
 * Three states:
 *   idle    — show the launch button + monthly-quota chip
 *   running — poll GET /api/research/query/:id every 2s, show step feed
 *   done    — show the case-file summary + proposals count + link to inbox
 *
 * Proposals land in the suggestions inbox with origin='agent' — we don't
 * render them inline here beyond a count. SuggestionsInboxPage handles
 * grouping / bulk-approve / confidence chips.
 */
const POLL_MS = 2000
const STEP_TYPE_LABEL = {
  plan:         'Planning',
  tool_call:    'Investigating',
  reconcile:    'Cross-referencing',
  hypothesise:  'Hunting gaps',
  report:       'Writing report',
}
const PHASE_ORDER = ['plan', 'tool_call', 'reconcile', 'hypothesise', 'report']
const PHASE_LABEL = {
  plan: 'Planning the investigation',
  tool_call: 'Searching sources',
  reconcile: 'Cross-referencing evidence',
  hypothesise: 'Hunting for gaps',
  report: 'Writing the case file',
}

export default function OriginResearchPanel({ personId, personName, treeId, canResearch = true, fromMysteryId = null }) {
  const [usage, setUsage] = useState(null)
  const [run, setRun] = useState(null)
  const [steps, setSteps] = useState([])
  const [err, setErr] = useState(null)
  const [starting, setStarting] = useState(false)
  const pollRef = useRef(null)
  // Id of the run the UI is currently tracking. In-flight poll ticks check
  // this before applying their result, so a tick that resolves AFTER a
  // cancel/reset can't write stale server state back over the idle view.
  const activeQueryRef = useRef(null)

  useEffect(() => {
    refreshUsage()
    // Auto-load the latest run for this person. /research/recent only
    // returns a 280-char summary_preview (cheap enough to list across
    // the dashboard); when we find one for this person we follow up
    // with /research/query/:id to get the full summary so FinishedView
    // doesn't render a half-sentence.
    api.get(`/research/recent`)
      .then(data => {
        const forThis = (data.runs || []).find(r => r.target_person_id === personId)
        if (!forThis) return
        if (['queued', 'running'].includes(forThis.status)) {
          // Provisional row from the preview is fine for the running view —
          // polling will overwrite with the full payload anyway.
          setRun({
            id: forThis.id,
            status: forThis.status,
            summary: forThis.summary_preview,
            proposals_count: forThis.proposals_count,
            cost_cents: forThis.cost_cents,
            error: null,
          })
          beginPolling(forThis.id)
        } else {
          // Terminal run — fetch the full record + steps so the case
          // file isn't truncated to 280 chars.
          Promise.all([
            api.get(`/research/query/${forThis.id}`),
            api.get(`/research/query/${forThis.id}/steps`),
          ])
            .then(([fullRun, s]) => {
              setRun(fullRun)
              setSteps(s.steps || [])
            })
            .catch(() => {})
        }
      })
      .catch(() => {})
    return () => clearInterval(pollRef.current)
  }, [personId])

  function refreshUsage() {
    api.get('/research/usage')
      .then(setUsage)
      .catch(() => {}) // silent — quota is optional context
  }

  async function startRun() {
    setErr(null)
    setStarting(true)
    try {
      const { query_id } = await api.post('/research/query', {
        target_person_id: personId,
        tree_id: treeId,  // critical: agent needs this to insert proposals into the right tree
        prompt: fromMysteryId
          ? `Solve the open mystery on ${personName}. Hunt vertical + horizontal gaps; cite every claim.`
          : `Investigate ${personName}. Hunt vertical + horizontal gaps.`,
        from_mystery_id: fromMysteryId || undefined,
      })
      setRun({ id: query_id, status: 'queued' })
      setSteps([])
      beginPolling(query_id)
    } catch (e) {
      setErr(e)
    } finally {
      setStarting(false)
    }
  }

  function beginPolling(queryId) {
    clearInterval(pollRef.current)
    activeQueryRef.current = queryId
    const tick = async () => {
      try {
        const [q, s] = await Promise.all([
          api.get(`/research/query/${queryId}`),
          api.get(`/research/query/${queryId}/steps`),
        ])
        // Ignore results if the user cancelled/reset (or started a
        // different run) while this request was in flight.
        if (activeQueryRef.current !== queryId) return
        setRun(q)
        setSteps(s.steps || [])
        if (['completed', 'failed', 'budget_exhausted', 'cancelled'].includes(q.status)) {
          clearInterval(pollRef.current)
          refreshUsage()
        }
      } catch (e) {
        if (activeQueryRef.current !== queryId) return
        clearInterval(pollRef.current)
        setErr(e)
      }
    }
    tick()
    pollRef.current = setInterval(tick, POLL_MS)
  }

  async function cancelRun() {
    if (!run) return
    const cancelId = run.id
    // Stop polling + detach this query so any in-flight tick is ignored.
    clearInterval(pollRef.current)
    activeQueryRef.current = null
    // Reset straight to idle FIRST so the "Research <name>" button is one
    // click away regardless of how the cancel request goes.
    setRun(null)
    setSteps([])
    setErr(null)
    try {
      await api.post(`/research/query/${cancelId}/cancel`)
    } catch {
      // Even if cancel fails (e.g. already terminal), the UI is already
      // idle and the agent honours cancellation between phases.
    }
  }

  if (!canResearch) return null

  // State machine:
  //   - if run is set AND status is terminal → finished
  //   - else if err is set AND no run → idle with error banner
  //   - else if run is set → running
  //   - else → idle
  // The old logic let err and running coexist, so "Starting agent..."
  // kept showing even after the server had set status='failed'.
  const terminal = ['completed', 'failed', 'budget_exhausted', 'cancelled']
  const finished = run && terminal.includes(run.status)
  const running  = run && !finished && !err
  const currentPhase = steps.length > 0 ? steps[steps.length - 1].step_type : null

  return (
    <section className="panel" style={panelStyle}>
      <header style={headerStyle}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '.6rem' }}>
          <span aria-hidden style={iconBadgeStyle}>🔍</span>
          <div>
            <div style={eyebrowStyle}>Origin AI · research agent {fromMysteryId && '· solving a mystery'}</div>
            <p style={{ margin: '.2rem 0 0', fontSize: '.85rem', color: 'var(--text-muted)', lineHeight: 1.4 }}>
              {fromMysteryId
                ? `You're working on another researcher's open lead. Your proposals carry your name and the originator reviews + credits your work.`
                : 'Plans, investigates, cross-references, hunts gaps, writes a case file.'}
            </p>
          </div>
        </div>
        {usage && (
          <span style={chipStyle(usage.remaining === 0)}>
            {usage.quota == null
              ? 'Unlimited'
              : `${usage.used} / ${usage.quota} this month`}
          </span>
        )}
      </header>
      <div style={dividerLineStyle} />

      {err?.code === 'TIER_LIMIT' && (
        <UpgradeInline tierError={err} />
      )}
      {err && err.code !== 'TIER_LIMIT' && (
        <div style={errorBoxStyle}>
          <div style={{ fontWeight: 600, marginBottom: '.25rem' }}>Something went wrong</div>
          <div style={{ fontSize: '.85rem' }}>{err.message || 'Unknown error.'}</div>
          <button
            onClick={() => { setErr(null); setRun(null); setSteps([]) }}
            style={dismissBtnStyle}
          >
            Dismiss
          </button>
        </div>
      )}

      {!run && !err && (
        <button
          className="btn btn-primary"
          onClick={startRun}
          disabled={starting || (usage && usage.quota != null && usage.remaining === 0)}
          style={{ marginTop: '1rem' }}
        >
          {starting ? 'Starting…' : `Research ${personName}`}
        </button>
      )}

      {running && (
        <div style={{ marginTop: '1rem' }}>
          <PhaseIndicator phase={currentPhase} stepCount={steps.length} />
          <ProgressFeed steps={steps} />
          <button className="btn btn-danger" onClick={cancelRun} style={cancelBtnStyle}>
            Cancel research
          </button>
        </div>
      )}

      {finished && (
        <FinishedView
          run={run}
          steps={steps}
          starting={starting}
          // One click re-runs: reset the tracked run, then immediately kick
          // off a fresh investigation on the same person. (Previously this
          // only returned to idle, so re-researching took two clicks and read
          // as "you can't research this person again".)
          onStartAnother={() => {
            activeQueryRef.current = null
            clearInterval(pollRef.current)
            setRun(null); setSteps([]); setErr(null)
            startRun()
          }}
        />
      )}
    </section>
  )
}

function PhaseIndicator({ phase, stepCount }) {
  const currentIdx = phase ? PHASE_ORDER.indexOf(phase) : -1
  return (
    <div style={{ marginBottom: '.75rem' }}>
      <div style={{ fontSize: '.9rem', fontWeight: 600, marginBottom: '.4rem' }}>
        {phase ? PHASE_LABEL[phase] : 'Starting agent…'}
        {stepCount > 0 && <span style={{ color: 'var(--text-muted)', fontWeight: 400, marginLeft: '.5rem' }}>step {stepCount}</span>}
      </div>
      <div style={{ display: 'flex', gap: '.3rem' }}>
        {PHASE_ORDER.map((p, i) => (
          <div key={p} style={{
            flex: 1,
            height: 4,
            borderRadius: 2,
            background: i <= currentIdx ? 'var(--accent, #c8a56e)' : 'var(--line, rgba(0,0,0,.08))',
            transition: 'background 200ms',
          }} title={PHASE_LABEL[p]} />
        ))}
      </div>
    </div>
  )
}

function ProgressFeed({ steps }) {
  return (
    <ol style={{ listStyle: 'none', padding: 0, margin: 0 }}>
      {steps.map(s => (
        <li key={s.id} style={stepStyle}>
          <span style={stepDotStyle(s.error)} aria-hidden>•</span>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: '.8rem', color: 'var(--muted)' }}>
              {STEP_TYPE_LABEL[s.step_type] || s.step_type}
              {s.tool_name ? ` · ${s.tool_name}` : ''}
            </div>
            {s.result_summary && (
              <div style={{ fontSize: '.88rem', marginTop: '.15rem' }}>
                {s.result_summary}
              </div>
            )}
          </div>
        </li>
      ))}
      {steps.length === 0 && (
        <li style={{ ...stepStyle, color: 'var(--muted)' }}>Starting agent…</li>
      )}
    </ol>
  )
}

function FinishedView({ run, steps, onStartAnother, starting }) {
  const statusLabels = {
    completed: { label: 'Research complete', tone: 'green' },
    budget_exhausted: {
      label: run.error === 'max_steps' ? 'Stopped at step cap' : 'Hit cost budget',
      tone: 'amber',
    },
    failed: { label: 'Run failed', tone: 'red' },
    cancelled: { label: 'Cancelled', tone: 'grey' },
  }
  const s = statusLabels[run.status] || statusLabels.failed
  const noProposals = run.proposals_count === 0 && run.status !== 'cancelled'
  return (
    <div style={{ marginTop: '1rem' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '.5rem' }}>
        <span className={`badge badge-${s.tone}`}>{s.label}</span>
        <span style={{ fontSize: '.8rem', color: 'var(--muted)' }}>
          {run.proposals_count} proposal{run.proposals_count === 1 ? '' : 's'}
          {' · '}
          {(run.cost_cents / 100).toFixed(2)} USD
        </span>
      </div>

      {run.summary && (
        <div style={summaryStyle}>
          {run.summary}
        </div>
      )}

      {noProposals && !run.summary && (
        <div style={{ ...summaryStyle, background: 'rgba(200, 150, 40, .08)' }}>
          The agent explored the available sources but didn't find enough evidence
          to propose concrete additions. This usually means the person's record isn't
          yet in the Knowledge Graph, Wikidata, or Delpher — or that searches need
          different spellings. Try running again with a different angle, or add more
          known details to the person first.
        </div>
      )}

      {run.error && run.status !== 'completed' && !['max_steps','budget'].includes(run.error) && (
        <div style={{ fontSize: '.85rem', color: 'var(--muted)', marginTop: '.5rem' }}>
          {run.error}
        </div>
      )}

      <details style={{ marginTop: '.75rem' }}>
        <summary style={{ fontSize: '.85rem', color: 'var(--muted)', cursor: 'pointer' }}>
          View audit trail ({steps.length} steps)
        </summary>
        <ol style={{ listStyle: 'none', padding: '.5rem 0 0', margin: 0 }}>
          {steps.map(s => (
            <li key={s.id} style={stepStyle}>
              <span style={stepDotStyle(s.error)} aria-hidden>•</span>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: '.8rem', color: 'var(--muted)' }}>
                  {STEP_TYPE_LABEL[s.step_type] || s.step_type}
                  {s.tool_name ? ` · ${s.tool_name}` : ''}
                </div>
                {s.result_summary && (
                  <div style={{ fontSize: '.85rem' }}>{s.result_summary}</div>
                )}
              </div>
            </li>
          ))}
        </ol>
      </details>

      <div style={{ display: 'flex', gap: '.5rem', marginTop: '1rem' }}>
        {run.proposals_count > 0 && (
          <a className="btn btn-primary" href="/suggestions">
            Review {run.proposals_count} proposal{run.proposals_count === 1 ? '' : 's'} →
          </a>
        )}
        <button className="btn btn-ghost" onClick={onStartAnother} disabled={starting}>
          {starting ? 'Starting…' : 'Research again'}
        </button>
      </div>

      {run.status === 'completed' && run.proposals_count > 0 && (
        <div style={marketplaceNudgeStyle}>
          <span style={marketplaceNudgeIcon} aria-hidden>🔎</span>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: '.86rem', color: 'var(--text)' }}>
              Want help solving these leads? <strong>Promote a research lead</strong> to the Mysteries marketplace or post a bounty — other researchers can pick it up.
            </div>
            <a href="/suggestions" style={marketplaceNudgeLink}>
              Open Suggestions inbox →
            </a>
          </div>
        </div>
      )}
    </div>
  )
}

const marketplaceNudgeStyle = {
  marginTop: '1rem',
  padding: '.75rem .95rem',
  background: 'linear-gradient(135deg, rgba(200,165,110,0.10), rgba(168,132,62,0.04))',
  border: '1px solid rgba(200,165,110,0.32)',
  borderRadius: 10,
  display: 'flex',
  alignItems: 'flex-start',
  gap: '.7rem',
}
const marketplaceNudgeIcon = {
  width: 32, height: 32, borderRadius: 8,
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
  background: 'rgba(200,165,110,0.2)', flexShrink: 0,
}
const marketplaceNudgeLink = {
  display: 'inline-block', marginTop: '.35rem',
  fontSize: '.8rem', color: '#a8843e', fontWeight: 600, textDecoration: 'none',
}

const panelStyle = {
  background: '#fff',
  borderRadius: 16,
  padding: '1.25rem 1.5rem 1.4rem',
  border: '1px solid var(--border, #e5e0d5)',
  boxShadow: '0 1px 3px rgba(60, 50, 30, 0.04), 0 4px 14px rgba(60, 50, 30, 0.04)',
}

const iconBadgeStyle = {
  width: 32,
  height: 32,
  borderRadius: 8,
  background: 'rgba(200,165,110,0.18)',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: '1rem',
  flexShrink: 0,
}

const eyebrowStyle = {
  fontSize: '.74rem',
  fontWeight: 700,
  letterSpacing: '0.13em',
  textTransform: 'uppercase',
  color: '#a8843e',
}

const dividerLineStyle = {
  height: 1,
  margin: '.85rem 0 1rem',
  background: 'linear-gradient(90deg, rgba(200,165,110,0.28) 0%, transparent 100%)',
}

const headerStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  gap: '1rem',
}

const chipStyle = (danger) => ({
  fontSize: '.75rem',
  padding: '.25rem .6rem',
  borderRadius: '999px',
  background: danger ? 'rgba(200, 70, 70, .12)' : 'var(--tint)',
  color: danger ? 'var(--err)' : 'var(--text)',
  whiteSpace: 'nowrap',
})

const stepStyle = {
  display: 'flex',
  alignItems: 'flex-start',
  gap: '.6rem',
  padding: '.4rem 0',
  borderBottom: '1px solid var(--line-faint, rgba(0,0,0,.06))',
}

const stepDotStyle = (err) => ({
  color: err ? 'var(--err)' : 'var(--accent)',
  fontSize: '1.2rem',
  lineHeight: 1,
  marginTop: '.1rem',
})

const summaryStyle = {
  marginTop: '.75rem',
  padding: '1rem',
  background: 'var(--tint)',
  borderRadius: '8px',
  fontSize: '.92rem',
  lineHeight: 1.5,
  whiteSpace: 'pre-wrap',
}

const errorBoxStyle = {
  marginTop: '.75rem',
  padding: '.85rem 1rem',
  background: 'rgba(200, 70, 70, .08)',
  border: '1px solid rgba(200, 70, 70, .25)',
  borderRadius: 8,
  color: 'var(--text)',
}

const cancelBtnStyle = {
  marginTop: '.75rem',
  background: 'rgba(200, 70, 70, .08)',
  color: 'var(--err, #b44)',
  border: '1px solid rgba(200, 70, 70, .3)',
  fontWeight: 600,
}

const dismissBtnStyle = {
  marginTop: '.6rem',
  padding: '.35rem .9rem',
  background: '#fff',
  color: 'var(--err, #b44)',
  border: '1px solid var(--err, #b44)',
  borderRadius: 6,
  fontWeight: 600,
  fontSize: '.85rem',
  cursor: 'pointer',
}
